import { createLandingDomain } from '@/api/landings';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import type { ILandingDomain } from '@/entities/landing.entity';
import { isValidLandingZoneName } from '@/pages/landings/lib/landing-url';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

interface AddLandingDomainModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated: (domain?: ILandingDomain) => void;
}

export default function AddLandingDomainModal({ open, onOpenChange, onCreated }: AddLandingDomainModalProps) {
	const [zoneName, setZoneName] = useState('');
	const [fieldError, setFieldError] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	function handleOpenChange(next: boolean) {
		if (!next) {
			setZoneName('');
			setFieldError('');
		}
		onOpenChange(next);
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		const trimmed = zoneName.trim().toLowerCase();
		if (!trimmed) {
			setFieldError('Введите домен');
			return;
		}
		if (!isValidLandingZoneName(trimmed)) {
			setFieldError('Введите корректный домен, например promo-example.com');
			return;
		}

		setFieldError('');
		setIsSaving(true);
		try {
			const created = await createLandingDomain(trimmed);
			toast.success('Домен добавлен');
			setZoneName('');
			onOpenChange(false);
			onCreated(created);
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className='sm:max-w-[440px]'>
				<DialogHeader>
					<DialogTitle>Добавить домен</DialogTitle>
					<DialogDescription>
						Домен будет добавлен в Cloudflare. После создания настройте NS-записи у регистратора.
					</DialogDescription>
				</DialogHeader>
				<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='landing-zone-name'>Домен</Label>
						<InputGroup>
							<InputGroupInput
								id='landing-zone-name'
								placeholder='promo-example.com'
								value={zoneName}
								onChange={e => {
									setZoneName(e.target.value);
									if (fieldError) setFieldError('');
								}}
								disabled={isSaving}
								autoComplete='off'
								aria-invalid={Boolean(fieldError)}
							/>
						</InputGroup>
						{fieldError ? (
							<p className='text-xs text-destructive'>{fieldError}</p>
						) : (
							<p className='text-xs text-muted-foreground'>Только корневой домен, без поддомена.</p>
						)}
					</div>
					<DialogFooter className='flex-col sm:flex-col'>
						<div className='w-full'>
							<LoadingWrapper show={isSaving}>
								<Button type='submit' className='w-full' disabled={isSaving}>
									Добавить
								</Button>
							</LoadingWrapper>
						</div>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
